/**
 * Lectura y veredicto de la prueba de eco.
 *
 * Los umbrales de latencia, perdida y jitter son los usuales de VoIP y se
 * ajustan aca con la experiencia; el veredicto describe lo medido, no de
 * quien es el problema.
 */

export interface Sample {
    received: number;
    lost: number;
    jitterMs: number;
    rttMs: number | null;
    /** IP publica con la que la central recibe el audio, si ya se conoce. */
    mediaIp?: string | null;
}

export type Verdict = 'ok' | 'warn' | 'bad' | 'noAudio';

export interface Summary {
    verdict: Verdict;
    lossPct: number;
    jitterMs: number;
    rttMs: number | null;
    /** Segundos sin audio nuevo despues de que el audio ya habia arrancado. */
    cutSeconds: number;
    /** IP publica que ve la central para el audio (candidato ICE en uso). */
    mediaIp: string | null;
}

export const WARN = {
    lossPct: 1, jitterMs: 20, rttMs: 180, cutSeconds: 1,
};
export const BAD = {
    lossPct: 5, jitterMs: 40, rttMs: 300, cutSeconds: 3,
};

export type Level = 'ok' | 'warn' | 'bad';

export function levelOf(value: number | null, key: keyof typeof WARN): Level {
    if (value === null) {
        return 'ok';
    }
    if (value >= BAD[key]) {
        return 'bad';
    }
    return value >= WARN[key] ? 'warn' : 'ok';
}

function isPublicIp(address: string): boolean {
    const octets = address.split('.').map(Number);
    if (octets.length === 4 && octets.every((octet) => octet >= 0 && octet <= 255)) {
        const [a, b] = octets;
        const privateRange = a === 10 || a === 127 || (a === 169 && b === 254)
            || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168) || (a === 100 && b >= 64 && b <= 127);

        return !privateRange;
    }

    // IPv6: ni local del enlace, ni unique-local, ni loopback
    return address.includes(':') && !/^(fe80|fc|fd|::1$)/i.test(address);
}

/**
 * La IP publica con la que la central recibe el audio: la direccion del
 * candidato ICE local en uso. Un candidato `relay` es la del TURN y uno `host`
 * privado o mDNS (nombre.local) no sale a internet: no sirven.
 */
export function publicAddressOf(candidate: any): string | null {
    const address = candidate && (candidate.address || candidate.ip);
    const type = candidate && candidate.candidateType;

    if (!address || !['srflx', 'prflx', 'host'].includes(type)) {
        return null;
    }

    return type === 'host' && !isPublicIp(address) ? null : address;
}

export function readSample(stats: RTCStatsReport): Sample {
    const sample: Sample = {
        received: 0, lost: 0, jitterMs: 0, rttMs: null, mediaIp: null,
    };
    let remoteRttMs: number | null = null;
    const reflexive: string[] = [];

    // El par que el transporte realmente eligio. Con IPv4 e IPv6 a la vez puede haber
    // varios pares "nominados y exitosos" y tomar cualquiera mezclaria la IP y la latencia.
    const transport: { pairId?: string } = {};
    stats.forEach((entry: any) => {
        if (entry.type === 'transport' && entry.selectedCandidatePairId) {
            transport.pairId = entry.selectedCandidatePairId;
        }
    });

    stats.forEach((entry: any) => {
        // La IP publica que el NAT le dio al navegador, descubierta por STUN.
        if (entry.type === 'local-candidate' && entry.candidateType === 'srflx') {
            const address = publicAddressOf(entry);
            if (address) {
                reflexive.push(address);
            }
        }
        if (entry.type === 'inbound-rtp' && entry.kind === 'audio') {
            sample.received += entry.packetsReceived ?? 0;
            sample.lost += Math.max(0, entry.packetsLost ?? 0);
            sample.jitterMs = Math.max(sample.jitterMs, (entry.jitter ?? 0) * 1000);
        }
        // Chrome no siempre informa `selected`: un par nominado y exitoso es el que se usa.
        // currentRoundTripTime solo aparece al cerrar una verificacion STUN: entre una
        // y otra viene vacio, y guardar 0 fabricaria caidas que no existen.
        const inUse = transport.pairId
            ? entry.id === transport.pairId
            : entry.selected || (entry.nominated && entry.state === 'succeeded');
        if (entry.type === 'candidate-pair' && inUse) {
            if (entry.currentRoundTripTime > 0) {
                sample.rttMs = Math.max(sample.rttMs ?? 0, entry.currentRoundTripTime * 1000);
            }
            sample.mediaIp = publicAddressOf(stats.get(entry.localCandidateId)) || sample.mediaIp;
        }
        // Lo que la central reporta por RTCP: es la ida y vuelta del audio, y sirve cuando
        // el par ICE todavia no midio. Solo existe si FreeSWITCH emite RTCP.
        if (entry.type === 'remote-inbound-rtp' && entry.kind === 'audio' && entry.roundTripTime > 0) {
            remoteRttMs = Math.max(remoteRttMs ?? 0, entry.roundTripTime * 1000);
        }
    });

    if (sample.rttMs === null) {
        sample.rttMs = remoteRttMs;
    }
    // Detras de un NAT el par en uso suele ser un `host` privado: la central ve igual la
    // direccion publica que el NAT le asigna, que es la del candidato reflexivo.
    if (!sample.mediaIp && reflexive.length) {
        [sample.mediaIp] = reflexive;
    }

    return sample;
}

/**
 * Estado de cada segundo. 'waiting' es el arranque de la llamada, antes del
 * primer audio: no es una falla y no se pinta como corte.
 */
export type FlowState = 'waiting' | 'ok' | 'cut';

export interface Series {
    loss: number[];
    jitter: number[];
    rtt: number[];
    /** Paquetes de audio que llegaron en ese segundo (no el acumulado). */
    received: number[];
    states: FlowState[];
}

/**
 * Una serie por segundo para graficar. La perdida es la de cada intervalo
 * (no la acumulada, que taparia un corte reciente) y la latencia arrastra el
 * ultimo valor medido cuando ese segundo no trae uno.
 */
export function buildSeries(samples: Sample[]): Series {
    const series: Series = {
        loss: [], jitter: [], rtt: [], received: [], states: [],
    };
    let lastRtt = 0;
    let audioStarted = false;

    samples.forEach((sample, index) => {
        const previous = samples[index - 1];
        const received = sample.received - (previous ? previous.received : 0);
        const lost = Math.max(0, sample.lost - (previous ? previous.lost : 0));
        const total = received + lost;

        series.loss.push(total > 0 ? (lost / total) * 100 : 0);
        series.jitter.push(sample.jitterMs);
        series.received.push(Math.max(0, received));
        lastRtt = sample.rttMs ?? lastRtt;
        series.rtt.push(lastRtt);
        audioStarted = audioStarted || received > 0;
        if (!audioStarted) {
            series.states.push('waiting');
        } else {
            series.states.push(received > 0 ? 'ok' : 'cut');
        }
    });

    return series;
}

/** La ultima IP de media conocida: el par ICE puede tardar en elegirse. */
export function latestMediaIp(samples: Sample[]): string | null {
    const known = samples.filter((sample) => sample.mediaIp);

    return known.length ? (known[known.length - 1].mediaIp as string) : null;
}

export function summarize(samples: Sample[]): Summary {
    const last = samples[samples.length - 1];
    const total = last ? last.received + last.lost : 0;
    const lossPct = total > 0 ? (last.lost / total) * 100 : 0;
    const jitterMs = Math.max(0, ...samples.map((s) => s.jitterMs));
    const rtts = samples.reduce<number[]>((acc, s) => (s.rttMs === null ? acc : [...acc, s.rttMs]), []);
    const rttMs = rtts.length > 0 ? rtts.reduce((a, b) => a + b, 0) / rtts.length : null;
    const cutSeconds = buildSeries(samples).states.filter((state) => state === 'cut').length;
    const mediaIp = latestMediaIp(samples);

    if (!last || last.received === 0) {
        return {
            verdict: 'noAudio', lossPct, jitterMs, rttMs, cutSeconds, mediaIp,
        };
    }

    const over = (limit: typeof WARN) => lossPct >= limit.lossPct
        || jitterMs >= limit.jitterMs
        || (rttMs !== null && rttMs >= limit.rttMs)
        || cutSeconds >= limit.cutSeconds;

    let verdict: Verdict = 'ok';
    if (over(BAD)) {
        verdict = 'bad';
    } else if (over(WARN)) {
        verdict = 'warn';
    }

    return {
        verdict, lossPct, jitterMs, rttMs, cutSeconds, mediaIp,
    };
}

/**
 * Puntaje de calidad de voz (MOS, 1 a 4.5) con el modelo E simplificado de ITU-T G.107,
 * el mismo que muestran Twilio y Telnyx. La latencia que entra es la de ida y vuelta.
 */
export function estimateMos(rttMs: number, jitterMs: number, lossPct: number): number {
    const effective = rttMs / 2 + jitterMs * 2 + 10;
    const base = effective < 160 ? 93.2 - effective / 40 : 93.2 - (effective - 120) / 10;
    const r = Math.max(0, Math.min(100, base - lossPct * 2.5));
    const mos = 1 + 0.035 * r + 0.000007 * r * (r - 60) * (100 - r);

    return Math.max(1, Math.min(4.5, mos));
}
